import React from 'react';
import {
  TruckIcon,
  ExclamationTriangleIcon,
  ChartBarIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';
import { TimeDisplay } from '../schedule/TimeDisplay';
import { useCaseHelpers } from '../../hooks/useCaseHelpers';
import { formatDateForDisplay } from '../../utils/dateUtils';
import { CASE_STATUS_LABELS, REMARKS_LABELS } from '../../constants/dashboard';
import type { Case } from '../../types';

interface ScheduleSummaryProps {
  cases: Case[];
  selectedDate: string;
}

/**
 * 閲覧者向けのスケジュール概要表示コンポーネント
 * 全体の進捗状況とハイライト情報に焦点を当てた読み取り専用表示
 */
export const ScheduleSummary: React.FC<ScheduleSummaryProps> = ({ cases, selectedDate }) => {
  const {
    safe: { getVehicleName, getBillingPartnerName },
  } = useCaseHelpers();

  // ステータス別の案件数を計算
  const statusSummary = {
    assigned: cases.filter(c => c.status === 'assigned').length,
    in_transit: cases.filter(c => c.status === 'in_transit').length,
    completed: cases.filter(c => c.status === 'completed').length,
    unassigned: cases.filter(c => !c.assignedLandTransporterId).length, // FL-142: 未割当判定
  };

  // 緊急案件を抽出
  const urgentCases = cases.filter(case_ => case_.remarks?.includes(REMARKS_LABELS.URGENT_KEYWORD));

  // 案件がない場合の表示
  if (cases.length === 0) {
    return (
      <div className='text-center py-12'>
        <ChartBarIcon className='mx-auto h-16 w-16 text-gray-400 dark:text-gray-600 mb-4' />
        <h3 className='text-lg font-medium text-gray-900 dark:text-white mb-2'>
          {formatDateForDisplay(selectedDate)}のスケジュール
        </h3>
        <p className='text-gray-500 dark:text-gray-400'>本日は配車予定がありません</p>
      </div>
    );
  }

  return (
    <div className='space-y-6'>
      {/* 進捗サマリー */}
      <div className='bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6'>
        <div className='flex items-center justify-between mb-4'>
          <h3 className='text-lg font-semibold text-gray-900 dark:text-white flex items-center'>
            <ChartBarIcon className='h-5 w-5 mr-2' />
            {formatDateForDisplay(selectedDate)}の進捗状況
          </h3>
          <span className='text-sm text-gray-500 dark:text-gray-400'>全{cases.length}件</span>
        </div>

        <div className='grid grid-cols-2 md:grid-cols-4 gap-4'>
          <div className='text-center'>
            <div className='text-2xl font-bold text-blue-600 dark:text-blue-400'>
              {statusSummary.assigned}
            </div>
            <div className='text-sm text-gray-600 dark:text-gray-400'>
              {CASE_STATUS_LABELS.assigned}
            </div>
          </div>
          <div className='text-center'>
            <div className='text-2xl font-bold text-yellow-600 dark:text-yellow-400'>
              {statusSummary.in_transit}
            </div>
            <div className='text-sm text-gray-600 dark:text-gray-400'>
              {CASE_STATUS_LABELS.in_transit}
            </div>
          </div>
          <div className='text-center'>
            <div className='text-2xl font-bold text-green-600 dark:text-green-400'>
              {statusSummary.completed}
            </div>
            <div className='text-sm text-gray-600 dark:text-gray-400'>
              {CASE_STATUS_LABELS.completed}
            </div>
          </div>
          <div className='text-center'>
            <div className='text-2xl font-bold text-red-600 dark:text-red-400'>
              {statusSummary.unassigned}
            </div>
            <div className='text-sm text-gray-600 dark:text-gray-400'>未割当</div>
          </div>
        </div>

        {/* 進捗バー */}
        <div className='mt-4'>
          <div className='flex text-sm text-gray-600 dark:text-gray-400 mb-2'>
            <span>
              完了率:{' '}
              {cases.length > 0 ? Math.round((statusSummary.completed / cases.length) * 100) : 0}%
            </span>
          </div>
          <div className='w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2'>
            <div
              className='bg-green-600 h-2 rounded-full transition-all duration-300'
              style={{
                width: `${cases.length > 0 ? (statusSummary.completed / cases.length) * 100 : 0}%`,
              }}
            />
          </div>
        </div>
      </div>

      {/* 緊急案件アラート */}
      {urgentCases.length > 0 && (
        <div className='bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4'>
          <div className='flex items-center mb-3'>
            <ExclamationTriangleIcon className='h-5 w-5 text-red-600 dark:text-red-400 mr-2' />
            <h4 className='font-medium text-red-800 dark:text-red-200'>
              緊急案件 ({urgentCases.length}件)
            </h4>
          </div>
          <div className='space-y-2'>
            {urgentCases.slice(0, 3).map(case_ => (
              <div key={case_.id} className='text-sm text-red-700 dark:text-red-300'>
                <div className='font-medium'>{getBillingPartnerName(case_)}</div>
                <TimeDisplay
                  pickupTimeStart={case_.pickupTimeStart}
                  pickupTimeEnd={case_.pickupTimeEnd}
                  deliveryTime={case_.deliveryTime}
                  textSize='text-xs'
                  spacing='space-y-0.5'
                />
              </div>
            ))}
            {urgentCases.length > 3 && (
              <div className='text-xs text-red-600 dark:text-red-400'>
                他{urgentCases.length - 3}件...
              </div>
            )}
          </div>
        </div>
      )}

      {/* 時間別概要 */}
      <div className='bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6'>
        <h4 className='font-medium text-gray-900 dark:text-white mb-4 flex items-center'>
          <ClockIcon className='h-5 w-5 mr-2' />
          時間別案件概要
        </h4>
        <div className='space-y-3'>
          {cases.slice(0, 5).map(case_ => (
            <div
              key={case_.id}
              className='flex items-center justify-between py-2 border-b border-gray-100 dark:border-gray-700 last:border-b-0'
            >
              <div className='flex items-center space-x-3 flex-1 min-w-0'>
                <TruckIcon className='h-4 w-4 text-gray-400 dark:text-gray-500 flex-shrink-0' />
                <div className='truncate'>
                  <div className='font-medium text-gray-900 dark:text-white text-sm truncate'>
                    {getBillingPartnerName(case_)}
                  </div>
                  <div className='text-xs text-gray-500 dark:text-gray-400 truncate'>
                    {getVehicleName(case_)}
                  </div>
                </div>
              </div>
              <div className='text-xs text-gray-600 dark:text-gray-400'>
                <TimeDisplay
                  pickupTimeStart={case_.pickupTimeStart}
                  pickupTimeEnd={case_.pickupTimeEnd}
                  deliveryTime={case_.deliveryTime}
                  textSize='text-xs'
                  spacing='space-y-0.5'
                />
              </div>
              <div className='ml-4 flex-shrink-0'>
                <span
                  className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                    {
                      provisional:
                        'bg-gray-100 dark:bg-gray-900/30 text-gray-800 dark:text-gray-400',
                      confirmed: 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-400',
                      assigned: 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-400',
                      picked_up:
                        'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-800 dark:text-indigo-400',
                      in_transit:
                        'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-400',
                      delivered:
                        'bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-400',
                      reported: 'bg-teal-100 dark:bg-teal-900/30 text-teal-800 dark:text-teal-400',
                      completed:
                        'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-400',
                    }[case_.status]
                  }`}
                >
                  {CASE_STATUS_LABELS[case_.status]}
                </span>
              </div>
            </div>
          ))}
          {cases.length > 5 && (
            <div className='text-center text-sm text-gray-500 dark:text-gray-400 pt-2'>
              他{cases.length - 5}件...
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
